import { Typography } from "@mui/material";
import { GridRenderCellParams } from "@mui/x-data-grid";

import { useTheme } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { WidgetHeader } from "components/atoms/WidgetHeader";
import { WidgetPaper } from "components/atoms/WidgetPaper";
import { CloudDataGrid } from "components/modules/CloudDataGrid";
import { useDDFilter } from "components/modules/disinfectionFilter/DisinfectionFilterPanel";
import { graphql } from "gql";
import { DisinfectionFilter } from "gql/graphql";
import { useMemo } from "react";
import { FormattedMessage } from "react-intl";
import { useQueryWithSnack } from "utils/useQueryWithSnack";

const QUERY = graphql(`
  query DisinfectionByOperator($filter: DisinfectionFilter!) {
    disinfectionsGroupedByOperator(filter: $filter) {
      results {
        operator
        complete
        incomplete
        interruptions
        total
      }
      totalCount
    }
  }
`);

interface Props {
  hiddenFilter?: DisinfectionFilter;
}

export const DisinfectionsByOperatorTable = ({ hiddenFilter }: Props) => {
  const [filterParams, setFilterParams] = useDDFilter();

  const { data, loading } = useQueryWithSnack(QUERY, {
    variables: {
      filter: {
        ...filterParams,
        ...hiddenFilter,
      },
    },
  });

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: "operator",
        headerName: "Operator",
        flex: 1,
        maxWidth: 300,
        renderCell: (params: GridRenderCellParams) => (
          <Typography fontWeight={500}>{params.value}</Typography>
        ),
      },
      {
        field: "interruptions",
        headerName: "Interruptions",
        maxWidth: 100,
        type: "number",
        flex: 1,
        align: "center",
        renderCell: (params: GridRenderCellParams) => {
          const theme = useTheme();
          return (
            <Typography sx={{ color: theme.palette.warning.main }}>
              {params.value}
            </Typography>
          );
        },
      },
      {
        field: "incomplete",
        headerName: "Incomplete",
        maxWidth: 100,
        type: "number",
        flex: 1,
        align: "right",
        renderCell: (params: GridRenderCellParams) => {
          const theme = useTheme();
          return params.value > 0 ? (
            <Typography
              component="a"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setFilterParams({
                  ...filterParams,
                  operators: [params.row.operator],
                  disinfectionStatus: "InComplete",
                });
              }}
              sx={{ color: theme.palette.error.main }}
            >
              {params.value}
            </Typography>
          ) : (
            <Typography sx={{ color: theme.palette.error.main }}>
              {params.value}
            </Typography>
          );
        },
      },
      {
        field: "complete",
        headerName: "Complete",
        maxWidth: 100,
        type: "number",
        flex: 1,
        align: "center",
        renderCell: (params: GridRenderCellParams) => {
          const theme = useTheme();
          return params.value > 0 ? (
            <Typography
              component="a"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setFilterParams({
                  ...filterParams,
                  operators: [params.row.operator],
                  disinfectionStatus: "Complete",
                });
              }}
              sx={{ color: theme.palette.success.main }}
            >
              {params.value}
            </Typography>
          ) : (
            <Typography sx={{ color: theme.palette.success.main }}>
              {params.value}
            </Typography>
          );
        },
      },
      {
        field: "completionRate",
        headerName: "% Completion Rate",
        type: "number",
        maxWidth: 150,
        flex: 1,
        align: "left",
        headerAlign: "left",
        valueGetter: (params: GridRenderCellParams) => {
          const complete = params.row.complete;
          const incomplete = params.row.incomplete;
          const total = complete + incomplete;

          if (total === 0) return 0;

          return Number(((complete / total) * 100).toFixed(1));
        },
        renderCell: (params: GridRenderCellParams) => (
          <Typography>{params.value}%</Typography>
        ),
      },
      {
        field: "total",
        headerName: "Total",
        type: "number",
        maxWidth: 100,
        flex: 1,
      },
      {
        field: "percentage",
        headerName: "% of All",
        type: "number",
        maxWidth: 100,
        flex: 1,
        align: "left",
        headerAlign: "left",
        valueGetter: (params: GridRenderCellParams) => {
          const grandTotal =
            params.api.getRowModels().size > 0
              ? Array.from(params.api.getRowModels().values()).reduce(
                  (sum: number, row: any) => sum + row.total,
                  0,
                )
              : 0;
          return Number(((params.row.total / grandTotal) * 100).toFixed(1));
        },
        renderCell: (params: GridRenderCellParams) => (
          <Typography>{params.value}%</Typography>
        ),
      },
    ],
    [filterParams, setFilterParams],
  );

  return (
    <WidgetPaper>
      <WidgetHeader
        label={
          <FormattedMessage id="by-operator" defaultMessage="By Operator" />
        }
        description={
          <FormattedMessage
            id="by-operator-description"
            defaultMessage="Number of disinfection cycles per operator in this period"
          />
        }
      />
      <CloudDataGrid
        initialState={{
          sorting: {
            sortModel: [{ field: "total", sort: "desc" }],
          },
        }}
        rows={
          data?.disinfectionsGroupedByOperator.results.map((item) => ({
            ...item,
            id: item.operator,
          })) ?? []
        }
        columns={columns}
        rowCount={data?.disinfectionsGroupedByOperator?.results.length ?? 0}
        pageSize={5}
        loading={loading}
      />
    </WidgetPaper>
  );
};
